import type { ZoomLevel } from '../features/schedule-tasks/components/ZoomControl';

export const GANTT_TODAY_MARKER_ID = 'fbt-today-marker';

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}

function addDays(d: Date, n: number): Date {
  const next = new Date(d);
  next.setDate(next.getDate() + n);
  return next;
}

/** Monday-start week (matches typical Gantt week scales). */
function startOfWeekMonday(d: Date): Date {
  const day = startOfLocalDay(d);
  const dow = day.getDay(); // 0 Sun .. 6 Sat
  const offset = dow === 0 ? -6 : 1 - dow;
  return addDays(day, offset);
}

export type GanttTodayMarkerConfig = {
  id: string;
  start_date: Date;
  end_date?: Date;
  css: string;
  text: string;
  title: string;
};

/**
 * Today marker for the timeline.
 * Day: thin line at today.
 * Week/Month/Year: band covering the period that contains today (approximation).
 */
export function buildTodayMarker(zoomLevel: ZoomLevel, now = new Date()): GanttTodayMarkerConfig {
  const today = startOfLocalDay(now);

  if (zoomLevel === 'day') {
    return {
      id: GANTT_TODAY_MARKER_ID,
      start_date: today,
      css: 'gantt-today-marker gantt-today-marker--day',
      text: 'Today',
      title: 'Today',
    };
  }

  if (zoomLevel === 'week') {
    const start = startOfWeekMonday(today);
    const end = addDays(start, 7);
    return {
      id: GANTT_TODAY_MARKER_ID,
      start_date: start,
      end_date: end,
      css: 'gantt-today-marker gantt-today-marker--week',
      text: 'Today',
      title: 'Current week',
    };
  }

  if (zoomLevel === 'month') {
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    const end = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    return {
      id: GANTT_TODAY_MARKER_ID,
      start_date: start,
      end_date: end,
      css: 'gantt-today-marker gantt-today-marker--month',
      text: 'Today',
      title: 'Current month',
    };
  }

  // year
  const start = new Date(today.getFullYear(), 0, 1);
  const end = new Date(today.getFullYear() + 1, 0, 1);
  return {
    id: GANTT_TODAY_MARKER_ID,
    start_date: start,
    end_date: end,
    css: 'gantt-today-marker gantt-today-marker--year',
    text: 'Today',
    title: 'Current year',
  };
}

/** Whether a scale/timeline cell date falls in the "today" period for this zoom. */
export function isTodayScaleCell(date: Date, zoomLevel: ZoomLevel, now = new Date()): boolean {
  const marker = buildTodayMarker(zoomLevel, now);
  const t = date.getTime();
  const start = marker.start_date.getTime();
  const end = marker.end_date ? marker.end_date.getTime() : start + 24 * 60 * 60 * 1000;
  return t >= start && t < end;
}
